import { Invoice } from '../../domain/entities/Invoice';
import { InvoiceRepository } from '../../domain/repositories/InvoiceRepository';
import { ApiClient } from '../api/ApiClient';

// Tipo para la respuesta de la API
interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

/**
 * Implementación del repositorio de facturas que se conecta a la API
 */
export class ApiInvoiceRepository implements InvoiceRepository {
  private apiClient: ApiClient;

  constructor() {
    this.apiClient = new ApiClient();
  }

  /**
   * Busca una factura por su número
   * @param invoiceNumber - Número de la factura a buscar
   * @returns Promesa que resuelve a la factura si se encuentra, o null si no existe
   */
  async findByInvoiceNumber(invoiceNumber: string): Promise<Invoice | null> {
    try {
      // Verificar si el número de factura contiene un guión para separar prefijo y número
      let prefijo: string;
      let numero: string;

      if (invoiceNumber.includes('-')) {
        const [prefix, number] = invoiceNumber.split('-');
        prefijo = prefix;
        numero = number;
      } else {
        // Si no hay guión, intentar extraer el prefijo y número basado en el formato
        // Asumimos que el prefijo es la parte alfabética (letras) + números hasta el cambio de patrón
        const match = invoiceNumber.match(/^([A-Za-z]+\d*)(\d+)$/);
        if (match) {
          prefijo = match[1];
          numero = match[2];
        } else {
          // Si no podemos separar, usamos todo como número y prefijo vacío
          prefijo = '';
          numero = invoiceNumber;
        }
      }

      // Realizar la petición a la API
      const response = await this.apiClient.get<ApiResponse<Invoice>>(`ConsultarFactura/prefijo/${prefijo}/numero/${numero}`);

      
      // Verificar si la respuesta fue exitosa y hay datos
      if (response && response.success && response.data) {
        // Normalizar el estado de la factura para que coincida con los valores esperados en la app
        const invoiceData = {
          ...response.data,
          // Convertir el estado a minúsculas para que coincida con 'pending', 'paid', 'overdue'
          status: this.normalizeStatus(response.data.status)
        };
        
        return invoiceData as Invoice;
      }
      
      return null;
    } catch (error) {
      console.error('Error al buscar factura por número:', error);
      return null;
    }
  }

  /**
   * Normaliza el estado de la factura para que coincida con los valores esperados en la aplicación
   * @param status - Estado de la factura como viene de la API
   * @returns Estado normalizado ('pending', 'paid', 'overdue')
   */
  private normalizeStatus(status: string): 'pending' | 'paid' | 'overdue' {
    const statusLower = status.toLowerCase();
    
    if (statusLower.includes('pend') || statusLower === 'pendiente') {
      return 'pending';
    }
    
    if (statusLower.includes('pag') || statusLower === 'pagada' || statusLower === 'pagado') {
      return 'paid';
    }
    
    if (statusLower.includes('venc') || statusLower === 'vencida' || statusLower === 'vencido') {
      return 'overdue';
    }
    
    // Por defecto, si no reconocemos el estado, lo marcamos como pendiente
    return 'pending';
  }

  /**
   * Obtiene todas las facturas
   * @returns Promesa que resuelve a un array de facturas
   */
  async getAll(): Promise<Invoice[]> {
    try {
      const response = await this.apiClient.get<ApiResponse<Invoice[]>>('/api/facturas');
      
      if (response && response.success && response.data) {
        // Normalizar el estado de todas las facturas
        return response.data.map(invoice => ({
          ...invoice,
          status: this.normalizeStatus(invoice.status)
        })) as Invoice[];
      }
      
      return [];
    } catch (error) {
      console.error('Error al obtener todas las facturas:', error);
      return [];
    }
  }

  /**
   * Actualiza una factura existente
   * @param updatedInvoice - Factura actualizada
   * @returns Promesa que resuelve a la factura actualizada
   */
  async update(updatedInvoice: Invoice): Promise<Invoice> {
    try {
      const response = await this.apiClient.put<ApiResponse<Invoice>>(`/api/facturas/${updatedInvoice.id}`, updatedInvoice);
      
      if (response && response.success && response.data) {
        return {
          ...response.data,
          status: this.normalizeStatus(response.data.status)
        } as Invoice;
      }
      
      throw new Error(`Error al actualizar factura con ID ${updatedInvoice.id}`);
    } catch (error) {
      console.error('Error al actualizar factura:', error);
      throw new Error(`Error al actualizar factura con ID ${updatedInvoice.id}`);
    }
  }
} 